import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { NotificationCenterService, UserNotificationDto } from '../notification-center.service';
import { BugRecordingService } from '../../../services/bug-recording.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-notification-bell',
  templateUrl: './notification-bell.component.html',
  styleUrls: ['./notification-bell.component.scss']
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  unreadCount = 0;
  recentNotifications: UserNotificationDto[] = [];
  showDropdown = false;
  loading = false;

  private subscriptions = new Subscription();

  constructor(
    private notificationService: NotificationCenterService,
    private router: Router,
    public recordingService: BugRecordingService
  ) {}

  ngOnInit(): void {
    this.loadUnreadCount();
    this.loadRecentNotifications();

    // Initialize notification service (starts polling)
    this.notificationService.initialize();

    // Subscribe to real-time notifications
    this.subscriptions.add(
      this.notificationService.notificationReceived$.subscribe(notification => {
        this.recentNotifications.unshift(notification);
        if (this.recentNotifications.length > 5) {
          this.recentNotifications = this.recentNotifications.slice(0, 5);
        }
        // Show browser notification if supported
        this.showBrowserNotification(notification);
      })
    );

    // Subscribe to unread count changes
    this.subscriptions.add(
      this.notificationService.unreadCountChanged$.subscribe(count => {
        this.unreadCount = count;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    // Stop the singleton-service polling timer when the bell is unmounted (logout / theme
    // switch). Without this each remount stacked another 10-min interval forever.
    this.notificationService.stopAutoRefresh();
  }

  loadUnreadCount(): void {
    this.notificationService.getUnreadCount().subscribe({
      next: (count) => {
        this.unreadCount = count;
      },
      error: (error) => {
        console.error('Failed to load unread count', error);
      }
    });
  }

  loadRecentNotifications(): void {
    this.loading = true;
    this.notificationService.getNotifications(0, 5, false).subscribe({
      next: (result) => {
        this.recentNotifications = result.notifications;
        this.loading = false;
      },
      error: (error) => {
        console.error('Failed to load recent notifications', error);
        this.loading = false;
      }
    });
  }

  toggleDropdown(): void {
    this.showDropdown = !this.showDropdown;
    if (this.showDropdown) {
      this.loadRecentNotifications();
    }
  }

  closeDropdown(): void {
    this.showDropdown = false;
  }

  onMarkAsRead(notification: UserNotificationDto, event: Event): void {
    event.stopPropagation();
    this.notificationService.markAsRead(notification.NotificationId).subscribe({
      next: (success) => {
        if (success) {
          notification.IsRead = true;
        }
      },
      error: (error) => {
        console.error('Failed to mark as read', error);
      }
    });
  }

  navigateToNotificationCenter(): void {
    this.closeDropdown();
    this.router.navigate(['/notification-center']);
  }

  onNotificationClick(notification: UserNotificationDto): void {
    if (!notification.IsRead) {
      this.notificationService.markAsRead(notification.NotificationId).subscribe({
        next: (success) => {
          if (success) {
            notification.IsRead = true;
          }
        }
      });
    }
    this.closeDropdown();
    if (notification.NavigationUrl) {
      this.router.navigateByUrl(notification.NavigationUrl);
    } else {
      this.router.navigate(['/notification-center']);
    }
  }

  formatDateTime(date?: Date): string {
    if (!date) return '';
    const d = new Date(date);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);

    if (minutes < 1) return 'Akkurat nå';
    if (minutes < 60) return `${minutes} min siden`;
    if (hours < 24) return `${hours} timer siden`;
    
    return d.toLocaleDateString('no-NO', { day: 'numeric', month: 'short' });
  }

  truncate(text: string, length: number = 60): string {
    if (!text || text.length <= length) return text;
    return text.substring(0, length) + '...';
  }

  private showBrowserNotification(notification: UserNotificationDto): void {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(notification.Subject, {
        body: this.stripHtml(notification.MessageBody),
        icon: '/assets/icons/notification-icon.png',
        badge: '/assets/icons/badge-icon.png'
      });
    } else if ('Notification' in window && Notification.permission !== 'denied') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          this.showBrowserNotification(notification);
        }
      });
    }
  }

  private stripHtml(html: string): string {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }
}
